import { parse } from 'tldts'

/**
 * Determines the redirect URL for a PawProtect subdomain request.
 *
 * Returns the full Embrace-domain URL to redirect to, or `null` if the
 * hostname is not a PawProtect subdomain and no redirect is needed.
 */
export function getPawProtectRedirectURL(
  hostname: string,
  protocol: string,
  port: string,
  fullPath: string
): string | null {
  // Parse the hostname to extract subdomain information
  const parsed = parse(`https://${hostname}`)

  // For localhost development, check if hostname starts with "pawprotect."
  let subdomain = ''
  if (hostname.includes('.localhost')) {
    // Extract subdomain from patterns like "pawprotect.localhost"
    subdomain = hostname.replace('.localhost', '')
  } else {
    // For regular domains, use tldts parsed subdomain
    subdomain = parsed?.subdomain || ''
  }

  // Only redirect when the subdomain contains "pawprotect" (case-insensitive)
  if (!subdomain || !subdomain.toLowerCase().includes('pawprotect')) {
    return null
  }

  // Determine the target domain
  let targetDomain = ''
  if (hostname.includes('.localhost')) {
    // For localhost, redirect to localhost:port (no "my." prefix)
    targetDomain = 'localhost'
  } else {
    // For all environments, redirect to my.embracepetinsurance.com
    targetDomain = 'my.embracepetinsurance.com'
  }

  // Preserve the original path and query params on the Embrace domain.
  // The auth middleware handles redirecting unauthenticated users to /login
  // for protected routes, while public routes like /reset-password are
  // accessible directly — no detour through /login required (RET-2262).
  const targetPath = fullPath === '/' ? '/login' : fullPath

  return `${protocol}//${targetDomain}${port}${targetPath}`
}

export default defineNuxtRouteMiddleware((to) => {
  // Only run on client side
  if (import.meta.server) return

  const redirectURL = getPawProtectRedirectURL(
    window.location.hostname,
    window.location.protocol,
    window.location.port ? `:${window.location.port}` : '',
    to.fullPath
  )

  if (redirectURL) {
    return navigateTo(redirectURL, {
      external: true
    })
  }
})
